import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import AHDate from "_shared/dates/date";
import { useGetEntityMemberPageQuery } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberQuery, { ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH } from "_shared/entity-members/_api/queries/entity-member-query";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import Guid from "_shared/guids/guid";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters, { PermissionOrderField } from "permissions/_api/queries/ordering/permission-order-params";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import { kindOfResource } from "permissions/_views/grant-row/grant-row-model";
import { readMembersSearch, withMembersSearch } from "permissions/_views/home/permissions-home-search";
import { kindPermissionsPath, offeredKindsByArea } from "permissions/_views/kinds/permission-kinds-model";
import { useKindHolderCount } from "permissions/_views/kinds/use-kind-holders";
import { OWN_PERMISSIONS_PATH, personPermissionsPath } from "permissions/_views/people/person-permissions-path";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import { personNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { personStateText } from "permissions/_views/targets/permission-target-state";
import { TEMPLATES_PATH } from "permissions/_views/templates/templates-paths";
import { ACCESS_REQUESTS_PATH } from "permissions/access-requests/access-requests-paths";
import { accessibleNames, grantDate, holdersLabel, kindAllows, kindLabel, memberRole } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router";

/**
 * The permissions home, at `/permissions` (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`,
 * "Page header, search and back"): the header with no subject and the search with the cursor in it,
 * then three ways in — the standing kinds by area with how many people hold each, the last ten grants
 * the viewer made, and the organization's members, searched and paged on the server.
 *
 * "Recently granted by you" is what the server can answer: grants whose grantor is the viewer, newest
 * first, with the ones since removed among them. A grant records who made it but not who removed it
 * or changed its level, so a change the viewer made to someone else's grant is not listed. Listing
 * those waits for grant history (#6162, "Later").
 *
 * Nothing is kept in browser storage: a list of recent people would outlive sign-out on a shared
 * browser. "Recently granted by you" is read from the server instead.
 */

export const RECENT_CHANGES = 10;
const MEMBERS_PAGE_SIZE = 10;
const MEMBER_SEARCH_DEBOUNCE_MS = 300;

const Page = styled(Box)({ height: "100%", minHeight: 0, overflowY: "auto", overflowX: "hidden" });

const Content = styled(Box)(({ theme }) => ({
  maxWidth: 1100,
  margin: "0 auto",
  padding: theme.spacing(2),
  display: "grid",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2, 3) },
}));

/** One column at xs, two from sm: each result is a two-line row with its count at the end. */
const ResultGrid = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "grid",
  gap: theme.spacing(0.5, 2),
  gridTemplateColumns: "minmax(0, 1fr)",
  [theme.breakpoints.up("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
}));

const ResultLink = styled(RouterLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minHeight: 44,
  padding: theme.spacing(1, 1),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  textDecoration: "none",
  "&:hover, &:focus-visible": { backgroundColor: theme.palette.action.hover },
}));

const Clamped = styled(Typography)({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
}) as typeof Typography;

function Section(props: Readonly<{ id: string; title: string; children: ReactNode; action?: ReactNode }>) {
  return (
    <Card variant="outlined" component="section" aria-labelledby={`${props.id}-title`} id={props.id}>
      <CardContent sx={{ display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Typography component="h2" variant="h6" id={`${props.id}-title`}>
            {props.title}
          </Typography>
          {props.action}
        </Box>
        {props.children}
      </CardContent>
    </Card>
  );
}

function ResultRow(props: Readonly<{ to: string; icon: ReactNode; primary: ReactNode; secondary?: string; trailing?: ReactNode }>) {
  return (
    <li>
      <ResultLink to={props.to}>
        {props.icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
            {props.primary}
          </Typography>
          {props.secondary && (
            <Clamped variant="caption" color="text.secondary" component="p">
              {props.secondary}
            </Clamped>
          )}
        </Box>
        {props.trailing}
      </ResultLink>
    </li>
  );
}

function KindRow(props: Readonly<{ kind: PermissionKind }>) {
  const { kind } = props;
  const count = useKindHolderCount(kind);
  const token = permissionAreaToken(kind.area);
  return (
    <ResultRow
      to={kindPermissionsPath(kind)}
      icon={<token.Icon fontSize="small" sx={{ color: token.color, flexShrink: 0 }} aria-hidden />}
      primary={kindLabel(kind)}
      secondary={kindAllows(kind)}
      trailing={
        count === undefined ? (
          <Skeleton width={56} />
        ) : (
          <Chip size="small" variant="outlined" label={holdersLabel(count)} sx={{ flexShrink: 0 }} />
        )
      }
    />
  );
}

function KindsCard(props: Readonly<{ areas: readonly string[]; kinds: readonly PermissionKind[]; mayAdministerPlatform: boolean }>) {
  const groups = offeredKindsByArea(props.areas, props.kinds, props.mayAdministerPlatform);
  return (
    <Section id="kinds" title="Kinds">
      {groups.map((group) => (
        <Box key={group.area} component="section" aria-label={group.area}>
          <Typography variant="overline" color="text.secondary" component="h3">
            {group.area}
          </Typography>
          <ResultGrid>
            {group.kinds.map((kind) => (
              <KindRow key={kind.slug} kind={kind} />
            ))}
          </ResultGrid>
        </Box>
      ))}
    </Section>
  );
}

/** The last grants the viewer made, removed ones included, newest first. */
export function recentChangesQuery(viewerUserId: Guid): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: RECENT_CHANGES }),
    orderParameters: new PermissionOrderParameters({ orderBy: PermissionOrderField.CreatedDate, orderDir: OrderDirection.Descending }),
    filterParameters: new PermissionFilterParameters({ grantorUserId: viewerUserId, showDisabled: true }),
  });
}

function RecentChangesCard(props: Readonly<{ viewerUserId: Guid; kinds: readonly PermissionKind[] }>) {
  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const query = useMemo(() => recentChangesQuery(props.viewerUserId), [props.viewerUserId]);
  const answer = useGetPermissionsQuery({ query }, { refetchOnFocus: true });
  const grants = useMemo(() => Permission.fromJSONArray(answer.data?.data ?? []), [answer.data]);
  const userIds = useMemo(() => new Set(grants.map((grant) => grant.granteeUserId.value)), [grants]);
  const names = usePersonNames(userIds);
  const anyFailed = [...names.states.values()].some((state) => state.state === "failed");

  let body: ReactNode;
  if (answer.error) {
    body = <Alert severity="error">{`Unable to load your recent changes. ${getErrorMessageFromUnknown(answer.error) ?? ""}`}</Alert>;
  } else if (answer.isLoading) {
    body = <Skeleton variant="rectangular" height={88} />;
  } else if (grants.length === 0) {
    body = <Typography color="text.secondary">You have not granted anything yet.</Typography>;
  } else {
    const today = AHDate.TODAY;
    body = (
      <ResultGrid>
        {grants.map((grant) => {
          const kind = kindOfResource(props.kinds, grant.resource);
          // A person the directory did not name says why, in place of the name (#6429).
          const state = personNameState(names, grant.granteeUserId.value);
          const name = state.state === "found" ? state.name : undefined;
          const primary = state.state === "loading" ? <Skeleton variant="text" width="8rem" aria-hidden /> : (name ?? personStateText(state));
          const when = grant.isDisabled
            ? `Removed ${grant.disabledDate ? grantDate(grant.disabledDate, today) : ""}`.trim()
            : `Granted ${grant.createdDate ? grantDate(grant.createdDate, today) : ""}`.trim();
          return (
            <ResultRow
              key={grant.id?.value}
              to={personPermissionsPath(grant.granteeUserId)}
              icon={<Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{name?.charAt(0)}</Avatar>}
              primary={primary}
              secondary={`${kind ? kindLabel(kind) : "A permission"} · ${when}`}
            />
          );
        })}
      </ResultGrid>
    );
  }
  return (
    <Section id="recent" title="Recently granted by you">
      {body}
      {/* Each row is a link, which cannot hold a button, so one Retry serves every name that failed. */}
      {anyFailed && (
        <Button size="small" onClick={names.retry} aria-label={accessibleNames.retryPerson} sx={{ mt: 1 }}>
          Retry
        </Button>
      )}
    </Section>
  );
}

function initials(member: EntityMemberSummary): string {
  return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
}

/**
 * The organization's members, filtered and paged on the server. The filter and the page are in the
 * query string: a page turned is a step Back undoes, and a filter replaces the address as it settles,
 * so a link to the card opens it as it was. The box keeps what is typed until it settles.
 */
function MembersCard(props: Readonly<{ legalEntityId: Guid }>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, pageIndex } = readMembersSearch(searchParams);
  const [text, setText] = useState(search);
  // When the address changes under the box — Back, or a link — the box follows it. Typing that
  // settles into the address changes `search` too, and then the box already says it.
  const [addressSearch, setAddressSearch] = useState(search);
  if (search !== addressSearch) {
    setAddressSearch(search);
    if (search !== text.trim()) setText(search);
  }

  useEffect(() => {
    const typed = text.trim();
    if (typed === search) return;
    const timeout = globalThis.setTimeout(() => {
      setSearchParams((current) => withMembersSearch(current, { search: typed, pageIndex: 0 }), { replace: true });
    }, MEMBER_SEARCH_DEBOUNCE_MS);
    return () => globalThis.clearTimeout(timeout);
  }, [text, search, setSearchParams]);

  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const query = useMemo(
    () =>
      new EntityMemberQuery({
        paginationParameters: PaginationParameters.create({ pageIndex, pageSize: MEMBERS_PAGE_SIZE }),
        query: search || undefined,
      }),
    [pageIndex, search],
  );
  const answer = useGetEntityMemberPageQuery({ legalEntityId: props.legalEntityId.value, query });
  const members = useMemo(() => EntityMemberSummary.fromJSONArray(answer.data?.data ?? []), [answer.data]);
  const total = answer.data?.headers.totalElements ?? 0;

  let body: ReactNode;
  if (answer.error) {
    body = <Alert severity="error">{`Unable to load the organization's members. ${getErrorMessageFromUnknown(answer.error) ?? ""}`}</Alert>;
  } else if (answer.isLoading) {
    body = <Skeleton variant="rectangular" height={120} />;
  } else if (members.length === 0) {
    body = <Typography color="text.secondary">{search ? "No member matches this search." : "The organization has no members."}</Typography>;
  } else {
    body = (
      <ResultGrid aria-label="Members">
        {members.map((member) => (
          <ResultRow
            key={member.userId.value}
            to={personPermissionsPath(member.userId)}
            icon={<Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials(member)}</Avatar>}
            primary={member.fullName}
            secondary={member.email}
            trailing={<Chip size="small" variant="outlined" label={memberRole(member.hasSigningAuthority)} sx={{ flexShrink: 0 }} />}
          />
        ))}
      </ResultGrid>
    );
  }

  return (
    <Section id="members" title="Members">
      <TextField
        size="small"
        label="Filter members by name or email"
        value={text}
        onChange={(event) => setText(event.target.value)}
        slotProps={{
          htmlInput: { maxLength: ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {body}
      {total > MEMBERS_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={total}
          page={pageIndex}
          rowsPerPage={MEMBERS_PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_, index) => setSearchParams((current) => withMembersSearch(current, { search, pageIndex: index }))}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} members`}
        />
      )}
    </Section>
  );
}

export default function PermissionsHomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionUser = useSessionUser();
  const kindsQuery = useGetPermissionKindsQuery();

  // The kind page's "Kinds" crumb comes back here, to the card.
  const hash = location.hash;
  const kindsLoaded = Boolean(kindsQuery.data);
  useEffect(() => {
    if (hash === "#kinds" && kindsLoaded) document.getElementById("kinds")?.scrollIntoView();
  }, [hash, kindsLoaded]);

  if (!sessionUser) return null;
  if (!sessionUser.canReadMemberPermissions()) return <NotAuthorized />;

  const mayAdministerPlatform = sessionUser.canAdministerPlatform();
  const organization = sessionUser.companyName;

  const actions = (
    <>
      <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => void navigate(OWN_PERMISSIONS_PATH)}>
        My permissions
      </Button>
      {/* The templates page asks for more than this page does: Member Permissions at read/write (#6826). */}
      {sessionUser.canManagePermissionTemplates() && (
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => void navigate(TEMPLATES_PATH)}>
          Templates
        </Button>
      )}
      {sessionUser.canDecideAccessRequests() && (
        <Button variant="outlined" startIcon={<HowToRegIcon />} onClick={() => void navigate(ACCESS_REQUESTS_PATH)}>
          Requests
        </Button>
      )}
    </>
  );

  let kinds: ReactNode;
  if (kindsQuery.error) {
    kinds = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void kindsQuery.refetch()}>
            Retry
          </Button>
        }
      >
        Unable to load the kinds of permission. {getErrorMessageFromUnknown(kindsQuery.error)}
      </Alert>
    );
  } else if (!kindsQuery.data) {
    kinds = <Skeleton variant="rectangular" height={240} />;
  } else {
    kinds = <KindsCard areas={kindsQuery.data.areas} kinds={kindsQuery.data.kinds} mayAdministerPlatform={mayAdministerPlatform} />;
  }

  return (
    <Page>
      <Content>
        <PermissionsSubjectHeader
          trail={[]}
          title="Permissions"
          secondary={organization ? `Who may see and do what in ${organization}` : undefined}
          actions={actions}
          focusSearch
        />
        {kinds}
        <RecentChangesCard viewerUserId={sessionUser.id ?? Guid.EMPTY} kinds={kindsQuery.data?.kinds ?? []} />
        <MembersCard legalEntityId={sessionUser.legalEntityId ?? Guid.EMPTY} />
      </Content>
    </Page>
  );
}
